// @ts-nocheck
import React, { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Loader2, Plus, Edit, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

interface EventType {
  id: string;
  name: string;
  created_at?: string;
}

const EventTypesManagement: React.FC = () => {
  const [eventTypes, setEventTypes] = useState<EventType[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [editingEventType, setEditingEventType] = useState<EventType | null>(null);
  const [newEventType, setNewEventType] = useState({ name: '' });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchEventTypes();
  }, []);

  const fetchEventTypes = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('event_types')
        .select('id, name, created_at')
        .order('name');
      
      if (error) throw error;
      setEventTypes(data || []);
    } catch (err) {
      console.error('Error fetching event types:', err);
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateEventType = async () => {
    if (!newEventType.name.trim()) {
      toast.error('Event type name is required');
      return;
    }

    try {
      setSubmitting(true);
      const { error } = await supabase
        .from('event_types')
        .insert([{
          name: newEventType.name.trim()
        }]);

      if (error) throw error;

      toast.success('Event type created successfully');
      setIsCreateDialogOpen(false);
      setNewEventType({ name: '' });
      fetchEventTypes();
    } catch (err) {
      console.error('Error creating event type:', err);
      toast.error(err instanceof Error ? err.message : 'Failed to create event type');
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdateEventType = async () => {
    if (!editingEventType || !editingEventType.name.trim()) {
      toast.error('Event type name is required');
      return;
    }

    try {
      setSubmitting(true);
      const { error } = await supabase
        .from('event_types')
        .update({
          name: editingEventType.name.trim()
        })
        .eq('id', editingEventType.id);

      if (error) throw error;

      toast.success('Event type updated successfully');
      setIsEditDialogOpen(false);
      setEditingEventType(null);
      fetchEventTypes();
    } catch (err) {
      console.error('Error updating event type:', err);
      toast.error(err instanceof Error ? err.message : 'Failed to update event type');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteEventType = async (eventType: EventType) => {
    try {
      const { error } = await supabase
        .from('event_types')
        .delete()
        .eq('id', eventType.id);

      if (error) throw error;

      toast.success('Event type deleted successfully');
      fetchEventTypes();
    } catch (err) {
      console.error('Error deleting event type:', err);
      toast.error(err instanceof Error ? err.message : 'Failed to delete event type');
    }
  };

  const openEditDialog = (eventType: EventType) => {
    setEditingEventType({ ...eventType });
    setIsEditDialogOpen(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="h-8 w-8 animate-spin" />
        <span className="ml-2">Loading event types...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <Card>
          <CardContent className="p-6">
            <p className="text-red-600">Error loading event types: {error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Event Types</h1>
          <p className="text-gray-600">Manage event types for the platform</p>
        </div>
        <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
          <DialogTrigger asChild>
            <Button>
              <Plus className="h-4 w-4 mr-2" />
              Add Event Type
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Create New Event Type</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name">Event Type Name</Label>
                <Input
                  id="name"
                  value={newEventType.name}
                  onChange={(e) => setNewEventType({ ...newEventType, name: e.target.value })}
                  placeholder="Enter event type name"
                />
              </div>
              <div className="flex justify-end space-x-2">
                <Button variant="outline" onClick={() => setIsCreateDialogOpen(false)}>
                  Cancel
                </Button>
                <Button onClick={handleCreateEventType} disabled={submitting}>
                  {submitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                  Create Event Type
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Event Types ({eventTypes.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {eventTypes.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Created At</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {eventTypes.map((eventType) => (
                  <TableRow key={eventType.id}>
                    <TableCell className="font-medium">
                      {eventType.name}
                    </TableCell>
                    <TableCell className="text-gray-500">
                      {eventType.created_at ? new Date(eventType.created_at).toLocaleDateString() : 'N/A'}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end space-x-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => openEditDialog(eventType)}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button variant="outline" size="sm">
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Delete Event Type</AlertDialogTitle>
                              <AlertDialogDescription>
                                Are you sure you want to delete "{eventType.name}"? This action cannot be undone.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction onClick={() => handleDeleteEventType(eventType)}>
                                Delete
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <div className="text-center py-8">
              <p className="text-gray-500 mb-4">No event types found</p>
              <Button onClick={() => setIsCreateDialogOpen(true)}>
                <Plus className="h-4 w-4 mr-2" />
                Create your first event type
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Edit Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Event Type</DialogTitle>
          </DialogHeader>
          {editingEventType && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="edit-name">Event Type Name</Label>
                <Input
                  id="edit-name"
                  value={editingEventType.name}
                  onChange={(e) => setEditingEventType({ ...editingEventType, name: e.target.value })}
                  placeholder="Enter event type name"
                />
              </div>
              <div className="flex justify-end space-x-2">
                <Button variant="outline" onClick={() => setIsEditDialogOpen(false)}>
                  Cancel
                </Button>
                <Button onClick={handleUpdateEventType} disabled={submitting}>
                  {submitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                  Update Event Type
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EventTypesManagement;
